import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { api } from '../api/client';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n';

// ── Server data shapes ──────────────────────────────────────────────

interface DimAgg { median: number; spread: number; flagged: boolean; n: number }

interface AnalysisResource {
  id: number;
  title: string;
  description: string;
  type: string;
  function: string;
  dims: Record<DimKey, DimAgg>;
  maturity: number | null;
  maturityN: number;
  raterCount: number;
  capabilities: { id: number; criticality: number }[];
}

interface AnalysisCapability { id: number; title: string; description: string; domain: string; nature: string }
interface AnalysisData { resources: AnalysisResource[]; capabilities: AnalysisCapability[] }

type DimKey = 'inimitability' | 'durability' | 'appropriability' | 'substitutability' | 'competitiveSuperiority';
type Channel = 'x' | 'y' | 'z' | 'size' | 'color';
type Tab = 'quadrant' | 'explorer' | 'capabilities';

const DIM_KEYS: DimKey[] = ['inimitability', 'durability', 'appropriability', 'substitutability', 'competitiveSuperiority'];
const DIM_SHORT: Record<DimKey, string> = {
  inimitability: 'I', durability: 'D', appropriability: 'A', substitutability: 'S', competitiveSuperiority: 'CS',
};

const RESOURCE_TYPES = ['Human', 'Organisational', 'Tangible', 'Intangible'];

// Chart-mark palette: the app's four resource-type hue families re-stepped for
// naked marks and validated (CVD + normal-vision floors) on both surfaces.
// Chips/badges elsewhere keep their own steps; hue family carries the identity.
const TYPE_COLORS: Record<string, { light: string; dark: string }> = {
  Human: { light: '#7c3aed', dark: '#8b6fe8' },
  Organisational: { light: '#008573', dark: '#009180' },
  Tangible: { light: '#a16207', dark: '#c98416' },
  Intangible: { light: '#db2777', dark: '#e25b97' },
};

// Sequential blue ramp (ordinal 1→5), validated per mode. On dark, magnitude runs
// dark→light so "near-surface" stays the low end.
const RAMP: Record<'light' | 'dark', string[]> = {
  light: ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab', '#104281'],
  dark: ['#184f95', '#256abf', '#3987e5', '#6da7ec', '#9ec5f4'],
};

function rampColor(value: number, theme: 'light' | 'dark'): string {
  const ramp = RAMP[theme];
  const t = Math.min(4, Math.max(0, value - 1));
  const i = Math.min(3, Math.floor(t));
  const f = t - i;
  const hex2rgb = (h: string) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
  const a = hex2rgb(ramp[i]);
  const b = hex2rgb(ramp[i + 1]);
  const mix = a.map((v, k) => Math.round(v + (b[k] - v) * f));
  return `rgb(${mix[0]},${mix[1]},${mix[2]})`;
}

function typeColor(type: string, theme: 'light' | 'dark'): string {
  return (TYPE_COLORS[type] || TYPE_COLORS.Organisational)[theme];
}

// Darken/lighten an rgb()/#hex color by factor f (0..~1.4) for face lighting.
function shade(color: string, f: number): string {
  let r = 0, g = 0, b = 0;
  const m = color.match(/rgb\((\d+),(\d+),(\d+)\)/);
  if (m) { r = +m[1]; g = +m[2]; b = +m[3]; }
  else if (color.startsWith('#')) {
    r = parseInt(color.slice(1, 3), 16); g = parseInt(color.slice(3, 5), 16); b = parseInt(color.slice(5, 7), 16);
  } else return color;
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v * f)));
  return `rgb(${c(r)},${c(g)},${c(b)})`;
}

// Flat 2-D marks (quadrant view + swatches). The explorer draws lit solids instead.
function ShapeMark({ type, x, y, r, fill, ring, ringWidth = 2 }: {
  type: string; x: number; y: number; r: number; fill: string; ring: string; ringWidth?: number;
}) {
  const common = { fill, stroke: ring, strokeWidth: ringWidth } as const;
  switch (type) {
    case 'Organisational': {
      const s = r * 1.72;
      return <rect x={x - s / 2} y={y - s / 2} width={s} height={s} {...common} />;
    }
    case 'Tangible': {
      const h = r * 1.9;
      return <path d={`M ${x} ${y - h * 0.62} L ${x + h * 0.58} ${y + h * 0.42} L ${x - h * 0.58} ${y + h * 0.42} Z`} {...common} strokeLinejoin="round" />;
    }
    case 'Intangible': {
      const d = r * 1.32;
      return <path d={`M ${x} ${y - d} L ${x + d} ${y} L ${x} ${y + d} L ${x - d} ${y} Z`} {...common} strokeLinejoin="round" />;
    }
    default:
      return <circle cx={x} cy={y} r={r} {...common} />;
  }
}

function TypeSwatch({ type, theme }: { type: string; theme: 'light' | 'dark' }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" style={{ flexShrink: 0 }}>
      <ShapeMark type={type} x={7} y={7} r={5} fill={typeColor(type, theme)} ring="transparent" ringWidth={0} />
    </svg>
  );
}

// Weighted composite over dimensions that actually have ratings.
function composite(r: AnalysisResource, weights: Record<DimKey, number>): number | null {
  let sum = 0;
  let wsum = 0;
  for (const key of DIM_KEYS) {
    const agg = r.dims[key];
    if (agg.n > 0 && weights[key] > 0) {
      sum += weights[key] * agg.median;
      wsum += weights[key];
    }
  }
  return wsum > 0 ? sum / wsum : null;
}

const DEFAULT_WEIGHTS: Record<DimKey, number> = {
  inimitability: 1, durability: 1, appropriability: 1, substitutability: 1, competitiveSuperiority: 1,
};

interface HoverTip { title: string; lines: string[]; x: number; y: number }

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const truncate = (s: string, n: number) => (s.length > n ? s.slice(0, n - 1) + '…' : s);

// Label layout: try anchor positions around each point (right, left, above,
// below, diagonals), treating already-placed labels AND the symbols themselves
// as obstacles. Labels that land away from their point get a leader line.
interface LabelBox { x1: number; y1: number; x2: number; y2: number }
interface LaidLabel {
  id: number; text: string; x: number; y: number;
  anchor: 'start' | 'middle' | 'end'; alpha: number;
  leader?: { x1: number; y1: number; x2: number; y2: number };
}
function layoutLabels(
  pts: Array<{ id: number; text: string; px: number; py: number; r: number; alpha?: number }>,
  W: number, H: number,
): LaidLabel[] {
  const pad = 2;
  const obstacles: LabelBox[] = pts.map(p => ({ x1: p.px - p.r - pad, y1: p.py - p.r - pad, x2: p.px + p.r + pad, y2: p.py + p.r + pad }));
  const placed: LabelBox[] = [];
  const out: LaidLabel[] = [];
  const hits = (b: LabelBox, list: LabelBox[]) => list.some(o => b.x1 < o.x2 && o.x1 < b.x2 && b.y1 < o.y2 && o.y1 < b.y2);
  for (const p of pts) {
    const w = p.text.length * 5.4;
    const h = 10;
    const cands: Array<{ x: number; y: number; anchor: 'start' | 'middle' | 'end'; far: boolean }> = [
      { x: p.px + p.r + 6, y: p.py + 3, anchor: 'start', far: false },
      { x: p.px - p.r - 6, y: p.py + 3, anchor: 'end', far: false },
      { x: p.px, y: p.py - p.r - 7, anchor: 'middle', far: false },
      { x: p.px, y: p.py + p.r + 13, anchor: 'middle', far: false },
      { x: p.px + p.r + 6, y: p.py - 12, anchor: 'start', far: true },
      { x: p.px + p.r + 6, y: p.py + 17, anchor: 'start', far: true },
      { x: p.px - p.r - 6, y: p.py - 12, anchor: 'end', far: true },
      { x: p.px - p.r - 6, y: p.py + 17, anchor: 'end', far: true },
      { x: p.px + p.r + 6, y: p.py - 26, anchor: 'start', far: true },
      { x: p.px + p.r + 6, y: p.py + 31, anchor: 'start', far: true },
      { x: p.px - p.r - 6, y: p.py - 26, anchor: 'end', far: true },
      { x: p.px - p.r - 6, y: p.py + 31, anchor: 'end', far: true },
    ];
    let laid: LaidLabel | null = null;
    for (const c of cands) {
      const x1 = c.anchor === 'start' ? c.x : c.anchor === 'end' ? c.x - w : c.x - w / 2;
      const box = { x1, y1: c.y - h + 2, x2: x1 + w, y2: c.y + 2 };
      if (box.x1 < 2 || box.y1 < 4 || box.x2 > W - 2 || box.y2 > H - 2) continue;
      if (hits(box, placed) || hits(box, obstacles)) continue;
      placed.push(box);
      laid = { id: p.id, text: p.text, x: c.x, y: c.y, anchor: c.anchor, alpha: p.alpha ?? 1 };
      if (c.far) {
        const lx = c.anchor === 'end' ? box.x2 + 2 : c.anchor === 'start' ? box.x1 - 2 : c.x;
        laid.leader = { x1: p.px, y1: p.py, x2: lx, y2: c.y - 3 };
      }
      break;
    }
    if (!laid) {
      const x = p.px + p.r + 6;
      const y = clamp(p.py + 3, 14, H - 6);
      placed.push({ x1: x, y1: y - h + 2, x2: x + w, y2: y + 2 });
      laid = { id: p.id, text: p.text, x, y, anchor: 'start', alpha: p.alpha ?? 1 };
    }
    out.push(laid);
  }
  return out;
}

// Fit-to-viewport scale for a chart of natural size natW×natH inside container el.
function useFitScale(natW: number, natH: number, bottomPad = 250) {
  const ref = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(1);
  const compute = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.clientWidth - 18;
    const h = window.innerHeight - el.getBoundingClientRect().top - bottomPad + 160;
    setFit(clamp(Math.min(w / natW, Math.max(h, 320) / natH), 0.45, 2.2));
  }, [natW, natH, bottomPad]);
  useEffect(() => {
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [compute]);
  return { ref, fit, refit: compute };
}

// ── Page ────────────────────────────────────────────────────────────

export function StrategicPortfolioPage() {
  const { theme } = useTheme();
  const { t } = useT();
  const [data, setData] = useState<AnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<Tab>('quadrant');

  // Filters scope every view below them
  const [activeTypes, setActiveTypes] = useState<Set<string>>(new Set(RESOURCE_TYPES));
  const [capFilter, setCapFilter] = useState<number | 'all'>('all');

  // Composite weights (used by quadrant + capability views)
  const [weights, setWeights] = useState<Record<DimKey, number>>({ ...DEFAULT_WEIGHTS });
  const [weightsOpen, setWeightsOpen] = useState(false);

  const [selected, setSelected] = useState<AnalysisResource | null>(null);
  const [hover, setHover] = useState<HoverTip | null>(null);

  // Per-resource pinned labels (double-click a point to pin/unpin)
  const [pinned, setPinned] = useState<Set<number>>(new Set());
  const togglePin = useCallback((id: number) => {
    setPinned(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  useEffect(() => {
    api.getVRIOAnalysis()
      .then((d: any) => setData(d))
      .catch((err: any) => setError(err.message || 'Failed to load VRIO analysis'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.resources.filter(r => {
      if (!activeTypes.has(r.type)) return false;
      if (capFilter !== 'all' && !r.capabilities.some(c => c.id === capFilter)) return false;
      return true;
    });
  }, [data, activeTypes, capFilter]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of data?.resources || []) counts[r.type] = (counts[r.type] || 0) + 1;
    return counts;
  }, [data]);

  const toggleType = (typeKey: string) => {
    setActiveTypes(prev => {
      const next = new Set(prev);
      if (next.has(typeKey)) next.delete(typeKey); else next.add(typeKey);
      return next;
    });
  };

  const showTip = useCallback((tip: HoverTip | null) => setHover(tip), []);

  if (loading) {
    return (
      <div className="page-padded" style={{ display: 'flex', justifyContent: 'center', paddingTop: '4rem' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-padded">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  if (!data || data.resources.length === 0) {
    return (
      <div className="page-padded">
        <div className="detail-card" style={{ textAlign: 'center', padding: '3rem' }}>
          <h2 style={{ marginBottom: 'var(--s8)', color: 'var(--ink-2)' }}>{t('sp.empty.title')}</h2>
          <p style={{ color: 'var(--ink-3)' }}>{t('sp.empty.body')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-padded sp-page">
      <div style={{ marginBottom: 'var(--s16)' }}>
        <h1 className="page-title">{t('sp.title')}</h1>
        <p style={{ color: 'var(--ink-3)', fontSize: 'var(--t-13)', marginTop: 'var(--s4)' }}>
          {t('sp.subtitle')}
        </p>
      </div>

      {/* Filter row — scopes all views below */}
      <div className="sp-filter-bar">
        <div className="filter-chips">
          {RESOURCE_TYPES.map(rt => (
            <button
              key={rt}
              className={`chip sp-type-chip ${activeTypes.has(rt) ? 'sp-chip-on' : ''}`}
              onClick={() => toggleType(rt)}
              title={t('sp.filter.toggleType')}
            >
              <TypeSwatch type={rt} theme={theme} />
              {rt} ({typeCounts[rt] || 0})
            </button>
          ))}
        </div>

        <select
          className="form-select sp-cap-select"
          value={capFilter === 'all' ? 'all' : String(capFilter)}
          onChange={e => setCapFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
        >
          <option value="all">{t('sp.filter.allCapabilities')}</option>
          {data.capabilities.map(c => (
            <option key={c.id} value={c.id}>{c.title}</option>
          ))}
        </select>

        <button className={`btn btn-ghost btn-sm ${weightsOpen ? 'active' : ''}`} onClick={() => setWeightsOpen(!weightsOpen)}>
          {t('sp.weights.button')} {Object.values(weights).some(w => w !== 1) ? t('sp.weights.custom') : ''}
        </button>
      </div>

      {weightsOpen && (
        <div className="card sp-weights-panel">
          <div className="sp-weights-grid">
            {DIM_KEYS.map(k => (
              <label key={k} className="sp-weight-row">
                <span className="sp-weight-label">{t(`sp.dim.${k}`)}</span>
                <input
                  type="range" min={0} max={3} step={0.5}
                  value={weights[k]}
                  onChange={e => setWeights(w => ({ ...w, [k]: Number(e.target.value) }))}
                />
                <span className="sp-weight-value">{weights[k].toFixed(1)}</span>
              </label>
            ))}
          </div>
          <div className="sp-weights-footer">
            <span style={{ color: 'var(--ink-3)', fontSize: 'var(--t-11)' }}>
              {t('sp.weights.note')}
            </span>
            <button className="btn btn-ghost btn-sm" onClick={() => setWeights({ ...DEFAULT_WEIGHTS })}>{t('sp.weights.reset')}</button>
          </div>
        </div>
      )}

      <div className="tabs">
        <button className={`tab ${tab === 'quadrant' ? 'active' : ''}`} onClick={() => setTab('quadrant')}>{t('sp.tab.quadrant')}</button>
        <button className={`tab ${tab === 'explorer' ? 'active' : ''}`} onClick={() => setTab('explorer')}>{t('sp.tab.explorer')}</button>
        <button className={`tab ${tab === 'capabilities' ? 'active' : ''}`} onClick={() => setTab('capabilities')}>{t('sp.tab.capabilities')}</button>
      </div>

      {tab === 'quadrant' && (
        <QuadrantView
          items={filtered} weights={weights} theme={theme}
          selected={selected} onSelect={setSelected} onHover={showTip}
          pinned={pinned} onTogglePin={togglePin}
        />
      )}
      {tab === 'explorer' && (
        <ExplorerView
          items={filtered} theme={theme}
          selected={selected} onSelect={setSelected} onHover={showTip}
          pinned={pinned} onTogglePin={togglePin}
        />
      )}
      {tab === 'capabilities' && (
        <CapabilityView
          items={filtered} capabilities={data.capabilities} weights={weights} theme={theme}
          capFilter={capFilter} onSelectResource={setSelected}
        />
      )}

      {selected && tab !== 'capabilities' && (
        <ResourceDetailCard resource={selected} weights={weights} capabilities={data.capabilities} theme={theme} onClose={() => setSelected(null)} />
      )}

      {hover && (
        <div className="sp-hover-tip" style={{ left: Math.min(hover.x + 14, window.innerWidth - 280), top: Math.min(hover.y + 14, window.innerHeight - 160) }}>
          <div className="sp-hover-tip-title">{hover.title}</div>
          {hover.lines.map((l, i) => <div key={i} className="sp-hover-tip-line">{l}</div>)}
        </div>
      )}
    </div>
  );
}

// ── Tab 1: VRIO × Maturity quadrant ────────────────────────────────

function QuadrantView({ items, weights, theme, selected, onSelect, onHover, pinned, onTogglePin }: {
  items: AnalysisResource[];
  weights: Record<DimKey, number>;
  theme: 'light' | 'dark';
  selected: AnalysisResource | null;
  onSelect: (r: AnalysisResource | null) => void;
  onHover: (tip: HoverTip | null) => void;
  pinned: Set<number>;
  onTogglePin: (id: number) => void;
}) {
  const { t } = useT();
  const [vrioT, setVrioT] = useState(3);
  const [matT, setMatT] = useState(3);
  const [showTable, setShowTable] = useState(false);
  const [showLabels, setShowLabels] = useState(false);

  const W = 760, H = 540;
  const { ref: fitRef, fit, refit } = useFitScale(W, H);
  const [zoom, setZoom] = useState<number | null>(null); // null = follow fit
  const scale = zoom ?? fit;

  const pad = { l: 48, r: 20, t: 30, b: 48 };
  const sx = (v: number) => pad.l + ((v - 1) / 4) * (W - pad.l - pad.r);
  const sy = (v: number) => H - pad.b - ((v - 1) / 4) * (H - pad.t - pad.b);

  const plotted = useMemo(() => {
    const pts = items
      .map(r => ({ r, comp: composite(r, weights) }))
      .filter((p): p is { r: AnalysisResource; comp: number } => p.comp !== null && p.r.maturity !== null);

    // De-overlap exact coincident points with a small deterministic spiral
    const seen: Record<string, number> = {};
    return pts.map(p => {
      let cx = sx(p.comp);
      let cy = sy(p.r.maturity as number);
      const key = `${Math.round(cx)}:${Math.round(cy)}`;
      const k = seen[key] || 0;
      seen[key] = k + 1;
      if (k > 0) {
        const ang = k * 2.399963;
        const rad = Math.min(6 + 3 * k, 16);
        cx += Math.cos(ang) * rad;
        cy += Math.sin(ang) * rad;
      }
      return { ...p, cx, cy };
    });
  }, [items, weights]);

  const labels = useMemo(() => {
    const visible = plotted.filter(p => showLabels || pinned.has(p.r.id));
    return layoutLabels(
      visible.map(p => ({ id: p.r.id, text: truncate(p.r.title, 24), px: p.cx, py: p.cy, r: 8 })),
      W, H,
    );
  }, [plotted, showLabels, pinned]);

  const excluded = items.length - plotted.length;
  const surface = 'var(--surface)';

  const rows = useMemo(() =>
    items
      .map(r => ({ r, comp: composite(r, weights) }))
      .sort((a, b) => (b.comp ?? -1) - (a.comp ?? -1)),
  [items, weights]);

  return (
    <div>
      <div className="sp-view-controls">
        <label className="sp-inline-label">
          {t('sp.quadrant.vrioThreshold')}
          <input type="number" className="form-input sp-num" min={1} max={5} step={0.5} value={vrioT}
            onChange={e => setVrioT(clamp(Number(e.target.value) || 3, 1, 5))} />
        </label>
        <label className="sp-inline-label">
          {t('sp.quadrant.maturityThreshold')}
          <input type="number" className="form-input sp-num" min={1} max={5} step={0.5} value={matT}
            onChange={e => setMatT(clamp(Number(e.target.value) || 3, 1, 5))} />
        </label>
        <span className="sp-zoom-group">
          <button className="btn btn-ghost btn-sm" aria-label={t('sp.zoom.out')} onClick={() => setZoom(clamp(scale / 1.2, 0.4, 3))}>−</button>
          <span className="sp-zoom-val">{Math.round(scale * 100)} %</span>
          <button className="btn btn-ghost btn-sm" aria-label={t('sp.zoom.in')} onClick={() => setZoom(clamp(scale * 1.2, 0.4, 3))}>+</button>
          <button className="btn btn-ghost btn-sm" onClick={() => { setZoom(null); refit(); }}>{t('sp.zoom.fit')}</button>
        </span>
        <button className={`btn btn-ghost btn-sm ${showLabels ? 'active' : ''}`} onClick={() => setShowLabels(!showLabels)} title={t('sp.labels.pinHint')}>
          {t('sp.toggle.labels')}
        </button>
        {pinned.size > 0 && (
          <button className="btn btn-ghost btn-sm" onClick={() => { for (const id of [...pinned]) onTogglePin(id); }}>
            {t('sp.labels.clear')} ({pinned.size})
          </button>
        )}
        <span className="sp-hint">{t('sp.labels.pinHint')}</span>
        <button className={`btn btn-ghost btn-sm ${showTable ? 'active' : ''}`} onClick={() => setShowTable(!showTable)}>
          {showTable ? t('sp.quadrant.hideTable') : t('sp.quadrant.tableView')}
        </button>
        {excluded > 0 && (
          <span style={{ color: 'var(--ink-3)', fontSize: 'var(--t-11)' }}>
            {excluded} {t('sp.quadrant.excludedNote')}
          </span>
        )}
      </div>

      <div className="card sp-chart-card" ref={fitRef}>
        <div className="sp-chart-scroll">
        <svg viewBox={`0 0 ${W} ${H}`} width={W * scale} height={H * scale} className="sp-svg" role="img"
          aria-label={`${t('sp.axis.composite')} × ${t('sp.axis.maturity')}`}>
          {/* Gridlines */}
          {[1, 2, 3, 4, 5].map(v => (
            <g key={v}>
              <line x1={sx(v)} y1={pad.t} x2={sx(v)} y2={H - pad.b} className="sp-grid" vectorEffect="non-scaling-stroke" />
              <line x1={pad.l} y1={sy(v)} x2={W - pad.r} y2={sy(v)} className="sp-grid" vectorEffect="non-scaling-stroke" />
              <text x={sx(v)} y={H - pad.b + 18} className="sp-tick" textAnchor="middle">{v}</text>
              <text x={pad.l - 10} y={sy(v) + 4} className="sp-tick" textAnchor="end">{v}</text>
            </g>
          ))}

          {/* Threshold lines */}
          <line x1={sx(vrioT)} y1={pad.t} x2={sx(vrioT)} y2={H - pad.b} className="sp-threshold" vectorEffect="non-scaling-stroke" />
          <line x1={pad.l} y1={sy(matT)} x2={W - pad.r} y2={sy(matT)} className="sp-threshold" vectorEffect="non-scaling-stroke" />

          {/* Quadrant labels */}
          <text x={W - pad.r - 8} y={pad.t + 14} className="sp-quad-label" textAnchor="end">{t('sp.quadrant.strengths')}</text>
          <text x={W - pad.r - 8} y={H - pad.b - 8} className="sp-quad-label" textAnchor="end">{t('sp.quadrant.develop')}</text>
          <text x={pad.l + 8} y={pad.t + 14} className="sp-quad-label">{t('sp.quadrant.overinvest')}</text>
          <text x={pad.l + 8} y={H - pad.b - 8} className="sp-quad-label">{t('sp.quadrant.deprioritize')}</text>

          {/* Axis titles */}
          <text x={(pad.l + W - pad.r) / 2} y={H - 8} className="sp-axis-title" textAnchor="middle">{t('sp.axis.composite')}</text>
          <text x={14} y={(pad.t + H - pad.b) / 2} className="sp-axis-title" textAnchor="middle" transform={`rotate(-90 14 ${(pad.t + H - pad.b) / 2})`}>{t('sp.axis.maturity')}</text>

          {/* Marks */}
          {plotted.map(p => (
            <g key={p.r.id}>
              {selected?.id === p.r.id && (
                <circle cx={p.cx} cy={p.cy} r={13} fill="none" stroke="var(--accent)" strokeWidth={2} />
              )}
              <ShapeMark type={p.r.type} x={p.cx} y={p.cy} r={7} fill={typeColor(p.r.type, theme)} ring={surface} />
            </g>
          ))}

          {/* Labels (all or pinned) above all marks */}
          {labels.map(l => l.leader && (
            <line key={`ld${l.id}`} x1={l.leader.x1} y1={l.leader.y1} x2={l.leader.x2} y2={l.leader.y2} className="sp-leader" vectorEffect="non-scaling-stroke" />
          ))}
          {labels.map(l => (
            <text key={l.id} x={l.x} y={l.y} textAnchor={l.anchor} className="sp-plabel">{l.text}</text>
          ))}

          {/* Hit targets on top (≥24px) */}
          {plotted.map(p => (
            <circle
              key={p.r.id}
              cx={p.cx} cy={p.cy} r={13} fill="transparent" style={{ cursor: 'pointer' }}
              onPointerMove={e => onHover({
                title: p.r.title,
                lines: [
                  `${t('sp.axis.composite')} ${p.comp.toFixed(2)} · ${t('sp.axis.maturity')} ${(p.r.maturity as number).toFixed(1)}`,
                  `${p.r.type} · ${p.r.raterCount} ${t('sp.tip.raters')}`,
                ],
                x: e.clientX, y: e.clientY,
              })}
              onPointerLeave={() => onHover(null)}
              onClick={e => { if (e.detail > 1) return; onSelect(selected?.id === p.r.id ? null : p.r); onHover(null); }}
              onDoubleClick={() => onTogglePin(p.r.id)}
            />
          ))}
        </svg>
        </div>
      </div>

      {showTable && (
        <div className="card sp-table-card">
          <table className="sp-table">
            <thead>
              <tr>
                <th>{t('sp.table.resource')}</th><th>{t('sp.table.type')}</th>
                {DIM_KEYS.map(k => <th key={k} title={t(`sp.dim.${k}`)}>{DIM_SHORT[k]}</th>)}
                <th>Composite</th><th>{t('sp.axis.maturity')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ r, comp }) => (
                <tr key={r.id} onClick={() => onSelect(r)} style={{ cursor: 'pointer' }}>
                  <td>{r.title}</td>
                  <td><span className="sp-td-type"><TypeSwatch type={r.type} theme={theme} />{r.type}</span></td>
                  {DIM_KEYS.map(k => (
                    <td key={k} className="sp-td-num">
                      {r.dims[k].n > 0 ? r.dims[k].median.toFixed(1) : '—'}
                      {r.dims[k].flagged && <span className="sp-flag" title={`${t('sp.flag.disagree')} ${r.dims[k].spread}`}>⚑</span>}
                    </td>
                  ))}
                  <td className="sp-td-num">{comp !== null ? comp.toFixed(2) : '—'}</td>
                  <td className="sp-td-num">{r.maturity !== null ? r.maturity.toFixed(1) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Tab 2: 5-D explorer (perspective 3-D scene) ────────────────────

const DEFAULT_CHANNELS: Record<Channel, DimKey> = {
  x: 'inimitability',
  y: 'durability',
  z: 'appropriability',
  size: 'substitutability',
  color: 'competitiveSuperiority',
};

// World-space unit polyhedra (half-size 1, centered on origin). Each face lists
// vertex indices counter-clockwise seen from outside, plus its outward normal.
const SOLIDS: Record<string, { verts: number[][]; faces: { idx: number[]; n: number[] }[]; scale: number }> = {
  // Cube — Organisational
  Organisational: {
    scale: 0.78,
    verts: [[-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1], [-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1]],
    faces: [
      { idx: [0, 3, 2, 1], n: [0, 0, -1] }, { idx: [4, 5, 6, 7], n: [0, 0, 1] },
      { idx: [0, 1, 5, 4], n: [0, -1, 0] }, { idx: [2, 3, 7, 6], n: [0, 1, 0] },
      { idx: [1, 2, 6, 5], n: [1, 0, 0] }, { idx: [0, 4, 7, 3], n: [-1, 0, 0] },
    ],
  },
  // Square pyramid — Tangible
  Tangible: {
    scale: 1.05,
    verts: [[-1, -0.72, -1], [1, -0.72, -1], [1, -0.72, 1], [-1, -0.72, 1], [0, 1.28, 0]],
    faces: [
      { idx: [0, 3, 2, 1], n: [0, -1, 0] },
      { idx: [0, 1, 4], n: [0, 0.45, -0.89] }, { idx: [1, 2, 4], n: [0.89, 0.45, 0] },
      { idx: [2, 3, 4], n: [0, 0.45, 0.89] }, { idx: [3, 0, 4], n: [-0.89, 0.45, 0] },
    ],
  },
  // Octahedron — Intangible
  Intangible: {
    scale: 1.28,
    verts: [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]],
    faces: [
      { idx: [0, 2, 4], n: [1, 1, 1] }, { idx: [2, 1, 4], n: [-1, 1, 1] },
      { idx: [1, 3, 4], n: [-1, -1, 1] }, { idx: [3, 0, 4], n: [1, -1, 1] },
      { idx: [2, 0, 5], n: [1, 1, -1] }, { idx: [1, 2, 5], n: [-1, 1, -1] },
      { idx: [3, 1, 5], n: [-1, -1, -1] }, { idx: [0, 3, 5], n: [1, -1, -1] },
    ],
  },
};

function normalize(v: number[]): number[] {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
}

function ExplorerView({ items, theme, selected, onSelect, onHover, pinned, onTogglePin }: {
  items: AnalysisResource[];
  theme: 'light' | 'dark';
  selected: AnalysisResource | null;
  onSelect: (r: AnalysisResource | null) => void;
  onHover: (tip: HoverTip | null) => void;
  pinned: Set<number>;
  onTogglePin: (id: number) => void;
}) {
  const { t } = useT();
  const [channels, setChannels] = useState<Record<Channel, DimKey>>({ ...DEFAULT_CHANNELS });
  const [yaw, setYaw] = useState(-0.65);
  const [pitch, setPitch] = useState(0.38);
  const [zoom, setZoom] = useState(1);
  const [autoRotate, setAutoRotate] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [showGuides, setShowGuides] = useState(true);
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  const rafRef = useRef<number>(0);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!autoRotate) return;
    const step = () => {
      if (!dragRef.current) setYaw(y => y + 0.004);
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [autoRotate]);

  // Wheel zoom needs a non-passive listener (React's synthetic wheel is passive).
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setZoom(z => clamp(z * (e.deltaY < 0 ? 1.08 : 0.93), 0.4, 3));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const W = 760, H = 560;
  const { ref: fitRef, fit } = useFitScale(W, H, 300);
  const cx = W / 2, cyc = H / 2 + 8;
  const S = 92 * zoom;
  const F = 14; // focal distance in data units — mild perspective

  // world (centered on 3,3,3) → view space; vd > 0 is toward the viewer
  const view = useCallback((wx: number, wy: number, wz: number) => {
    const cosY = Math.cos(yaw), sinY = Math.sin(yaw);
    const x1 = cosY * wx + sinY * wz;
    const z1 = -sinY * wx + cosY * wz;
    const cosP = Math.cos(pitch), sinP = Math.sin(pitch);
    return { vx: x1, vy: cosP * wy - sinP * z1, vd: sinP * wy + cosP * z1 };
  }, [yaw, pitch]);

  const project = useCallback((dx: number, dy: number, dz: number) => {
    const v = view(dx - 3, dy - 3, dz - 3);
    const pf = F / (F - v.vd);
    return { px: cx + v.vx * S * pf, py: cyc - v.vy * S * pf, vd: v.vd, pf };
  }, [view, S, cx, cyc]);

  // Depth fade: far → 0.45, near → 1
  const fade = (vd: number) => clamp(0.45 + ((vd + 3.5) / 7) * 0.55, 0.45, 1);

  const plotted = useMemo(() => {
    const usable = items.filter(r =>
      (['x', 'y', 'z', 'size', 'color'] as Channel[]).every(ch => r.dims[channels[ch]].n > 0)
    );
    return usable
      .map(r => {
        const val = (ch: Channel) => r.dims[channels[ch]].median;
        const p = project(val('x'), val('y'), val('z'));
        const rPx = 5 + ((val('size') - 1) / 4) * 8; // 5..13 px at zoom 1
        return { r, ...p, dataX: val('x'), dataY: val('y'), dataZ: val('z'), rPx, colorV: val('color') };
      })
      .sort((a, b) => a.vd - b.vd); // far first
  }, [items, channels, project]);

  const excluded = items.length - plotted.length;

  // Cube frame edges
  const edges = useMemo(() => {
    const c = [1, 5];
    const pts: [number, number, number][] = [];
    for (const a of c) for (const b of c) for (const d of c) pts.push([a, b, d]);
    const lines: Array<[[number, number, number], [number, number, number]]> = [];
    for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        if (pts[i].filter((v, k) => v !== pts[j][k]).length === 1) lines.push([pts[i], pts[j]]);
      }
    }
    return lines;
  }, []);

  // Interior gridlines on the walls currently facing AWAY (they sit behind the
  // data, anchoring positions without occluding anything).
  const wallGrids = useMemo(() => {
    if (!showGuides) return [];
    const faces = [
      { n: [1, 0, 0], fix: ['x', 5] }, { n: [-1, 0, 0], fix: ['x', 1] },
      { n: [0, 1, 0], fix: ['y', 5] }, { n: [0, -1, 0], fix: ['y', 1] },
      { n: [0, 0, 1], fix: ['z', 5] }, { n: [0, 0, -1], fix: ['z', 1] },
    ] as const;
    const lines: Array<[number[], number[]]> = [];
    for (const f of faces) {
      if (view(f.n[0], f.n[1], f.n[2]).vd >= 0) continue; // facing viewer → skip
      const [axis, val] = f.fix;
      const others = (['x', 'y', 'z'] as const).filter(a => a !== axis);
      for (const g of [2, 3, 4]) {
        for (const [a, b] of [[others[0], others[1]], [others[1], others[0]]] as const) {
          const p1: Record<string, number> = { [axis]: val as number, [a]: g, [b]: 1 };
          const p2: Record<string, number> = { [axis]: val as number, [a]: g, [b]: 5 };
          lines.push([[p1.x, p1.y, p1.z], [p2.x, p2.y, p2.z]]);
        }
      }
    }
    return lines;
  }, [showGuides, view]);

  // Labels (all or pinned) with layout; near points get placement priority
  const labels = useMemo(() => {
    const visible = [...plotted].reverse().filter(p => showLabels || pinned.has(p.r.id));
    return layoutLabels(
      visible.map(p => ({
        id: p.r.id,
        text: truncate(p.r.title, 22),
        px: p.px, py: p.py,
        r: p.rPx * zoom * p.pf,
        alpha: Math.max(0.55, fade(p.vd)),
      })),
      W, H,
    );
  }, [plotted, showLabels, pinned, zoom]);

  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    dragRef.current = { x: e.clientX, y: e.clientY };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.x;
    const dy = e.clientY - dragRef.current.y;
    dragRef.current = { x: e.clientX, y: e.clientY };
    setYaw(y => y + dx * 0.008);
    setPitch(p => clamp(p + dy * 0.008, -1.35, 1.35));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const dimLabel = (k: DimKey) => t(`sp.dim.${k}`);
  const channelLabel = (ch: Channel) =>
    ch === 'size' ? t('sp.explorer.dotSize') : ch === 'color' ? t('sp.explorer.dotColor') : t(`sp.explorer.${ch}Axis`);

  const L = normalize([-0.35, 0.55, 0.75]); // light direction in view space
  const surface = 'var(--surface)';
  const axisTicks = [1, 3, 5];

  // Render one lit solid for a data point
  const renderSolid = (p: typeof plotted[number]) => {
    const base = rampColor(p.colorV, theme);
    const screenR = p.rPx * zoom * p.pf;
    if (p.r.type === 'Human') {
      return (
        <g>
          <circle cx={p.px} cy={p.py} r={screenR} fill={base} stroke={surface} strokeWidth={1.5} />
          <circle cx={p.px} cy={p.py} r={screenR} fill="url(#sp-sphere-shade)" />
        </g>
      );
    }
    const solid = SOLIDS[p.r.type] || SOLIDS.Organisational;
    const h = (p.rPx / 92) * solid.scale; // half-size in data units
    const polys = solid.faces
      .map(face => {
        const nv = view(face.n[0], face.n[1], face.n[2]);
        if (nv.vd <= 0.02) return null; // back face
        const lam = Math.max(0, nv.vx * L[0] + nv.vy * L[1] + nv.vd * L[2]);
        const fill = shade(base, 0.6 + 0.45 * lam);
        const pts = face.idx
          .map(i => solid.verts[i])
          .map(v => project(p.dataX + v[0] * h, p.dataY + v[1] * h, p.dataZ + v[2] * h))
          .map(q => `${q.px.toFixed(1)},${q.py.toFixed(1)}`)
          .join(' ');
        return { pts, fill, vd: nv.vd };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null)
      .sort((a, b) => a.vd - b.vd);
    return (
      <g>
        {polys.map((f, i) => (
          <polygon key={i} points={f.pts} fill={f.fill} stroke={surface} strokeWidth={0.8} strokeLinejoin="round" />
        ))}
      </g>
    );
  };

  return (
    <div>
      <div className="sp-view-controls">
        {(['x', 'y', 'z', 'size', 'color'] as Channel[]).map(ch => (
          <label key={ch} className="sp-inline-label">
            {channelLabel(ch)}
            <select
              className="form-select sp-channel-select"
              value={channels[ch]}
              onChange={e => setChannels(c => ({ ...c, [ch]: e.target.value as DimKey }))}
            >
              {DIM_KEYS.map(k => <option key={k} value={k}>{dimLabel(k)}</option>)}
            </select>
          </label>
        ))}
      </div>
      <div className="sp-view-controls">
        <span className="sp-zoom-group">
          <button className="btn btn-ghost btn-sm" aria-label={t('sp.zoom.out')} onClick={() => setZoom(z => clamp(z / 1.2, 0.4, 3))}>−</button>
          <span className="sp-zoom-val">{Math.round(zoom * 100)} %</span>
          <button className="btn btn-ghost btn-sm" aria-label={t('sp.zoom.in')} onClick={() => setZoom(z => clamp(z * 1.2, 0.4, 3))}>+</button>
        </span>
        <button className={`btn btn-ghost btn-sm ${showLabels ? 'active' : ''}`} onClick={() => setShowLabels(!showLabels)} title={t('sp.labels.pinHint')}>
          {t('sp.toggle.labels')}
        </button>
        {pinned.size > 0 && (
          <button className="btn btn-ghost btn-sm" onClick={() => { for (const id of [...pinned]) onTogglePin(id); }}>
            {t('sp.labels.clear')} ({pinned.size})
          </button>
        )}
        <button className={`btn btn-ghost btn-sm ${showGuides ? 'active' : ''}`} onClick={() => setShowGuides(!showGuides)}>
          {t('sp.toggle.guides')}
        </button>
        <button className={`btn btn-ghost btn-sm ${autoRotate ? 'active' : ''}`} onClick={() => setAutoRotate(!autoRotate)}>
          {autoRotate ? t('sp.explorer.stopRotate') : t('sp.explorer.autoRotate')}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => { setYaw(-0.65); setPitch(0.38); setZoom(1); }}>{t('sp.explorer.resetView')}</button>
      </div>

      <div className="card sp-chart-card" ref={fitRef}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`} width={W * fit} height={H * fit} className="sp-svg sp-svg-3d" role="img"
          aria-label={t('sp.tab.explorer')}
          onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
        >
          <defs>
            {/* Sphere shading: shared highlight/shadow overlay for any base color */}
            <radialGradient id="sp-sphere-shade" cx="0.35" cy="0.3" r="0.9">
              <stop offset="0%" stopColor="#fff" stopOpacity="0.55" />
              <stop offset="45%" stopColor="#fff" stopOpacity="0" />
              <stop offset="100%" stopColor="#000" stopOpacity="0.3" />
            </radialGradient>
          </defs>

          {/* Wall gridlines (back faces only) */}
          {wallGrids.map(([a, b], i) => {
            const pa = project(a[0], a[1], a[2]);
            const pb = project(b[0], b[1], b[2]);
            return <line key={`g${i}`} x1={pa.px} y1={pa.py} x2={pb.px} y2={pb.py} className="sp-grid" opacity={0.45} vectorEffect="non-scaling-stroke" />;
          })}

          {/* Cube frame */}
          {edges.map(([a, b], i) => {
            const pa = project(a[0], a[1], a[2]);
            const pb = project(b[0], b[1], b[2]);
            return <line key={i} x1={pa.px} y1={pa.py} x2={pb.px} y2={pb.py} className="sp-grid" vectorEffect="non-scaling-stroke" />;
          })}

          {/* Axis ticks along the three origin edges */}
          {axisTicks.map(tick => {
            const px = project(tick, 1, 1), py = project(1, tick, 1), pz = project(1, 1, tick);
            return (
              <g key={tick}>
                <text x={px.px} y={px.py + 14} className="sp-tick" textAnchor="middle">{tick}</text>
                <text x={py.px - 10} y={py.py + 4} className="sp-tick" textAnchor="end">{tick}</text>
                <text x={pz.px + 10} y={pz.py + 4} className="sp-tick">{tick}</text>
              </g>
            );
          })}

          {/* Axis titles just past the ends of the origin edges */}
          {(() => {
            const ex = project(5.7, 1, 1), ey = project(1, 5.55, 1), ez = project(1, 1, 5.7);
            return (
              <g>
                <text x={ex.px} y={ex.py} className="sp-axis-title" textAnchor="middle">{dimLabel(channels.x)}</text>
                <text x={ey.px} y={ey.py} className="sp-axis-title" textAnchor="middle">{dimLabel(channels.y)}</text>
                <text x={ez.px} y={ez.py} className="sp-axis-title" textAnchor="middle">{dimLabel(channels.z)}</text>
              </g>
            );
          })()}

          {/* Floor anchors: drop line + shadow pin each point to its (x,z) cell */}
          {showGuides && plotted.map(p => {
            const floor = project(p.dataX, 1, p.dataZ);
            const screenR = p.rPx * zoom * p.pf;
            const ry = Math.max(1.2, screenR * (0.2 + 0.3 * Math.abs(Math.sin(pitch))));
            return (
              <g key={`d${p.r.id}`} opacity={fade(p.vd)}>
                <line x1={p.px} y1={p.py} x2={floor.px} y2={floor.py} className="sp-dropline" vectorEffect="non-scaling-stroke" />
                <ellipse cx={floor.px} cy={floor.py} rx={screenR * 0.8} ry={ry} className="sp-shadow" />
              </g>
            );
          })}

          {/* Solids, far → near, depth-faded */}
          {plotted.map(p => {
            const screenR = p.rPx * zoom * p.pf;
            return (
              <g key={p.r.id} opacity={fade(p.vd)}>
                {selected?.id === p.r.id && (
                  <circle cx={p.px} cy={p.py} r={screenR + 6} fill="none" stroke="var(--accent)" strokeWidth={2} />
                )}
                {renderSolid(p)}
              </g>
            );
          })}

          {/* Labels (all or pinned) above everything */}
          {labels.map(l => l.leader && (
            <line key={`ld${l.id}`} x1={l.leader.x1} y1={l.leader.y1} x2={l.leader.x2} y2={l.leader.y2} className="sp-leader" opacity={l.alpha} vectorEffect="non-scaling-stroke" />
          ))}
          {labels.map(l => (
            <text key={l.id} x={l.x} y={l.y} textAnchor={l.anchor} className="sp-plabel" opacity={l.alpha}>{l.text}</text>
          ))}

          {/* Hit targets */}
          {plotted.map(p => {
            const screenR = p.rPx * zoom * p.pf;
            return (
              <circle
                key={p.r.id}
                cx={p.px} cy={p.py} r={Math.max(screenR + 6, 13)} fill="transparent" style={{ cursor: 'pointer' }}
                onPointerMove={e => {
                  if (dragRef.current) return;
                  onHover({
                    title: p.r.title,
                    lines: [
                      ...DIM_KEYS.map(k => `${dimLabel(k)} ${p.r.dims[k].n > 0 ? p.r.dims[k].median.toFixed(1) : '—'}`),
                      `${p.r.type} · ${p.r.raterCount} ${t('sp.tip.raters')}`,
                    ],
                    x: e.clientX, y: e.clientY,
                  });
                }}
                onPointerLeave={() => onHover(null)}
                onClick={e => { if (e.detail > 1) return; onSelect(selected?.id === p.r.id ? null : p.r); onHover(null); }}
                onDoubleClick={() => onTogglePin(p.r.id)}
              />
            );
          })}
        </svg>

        {/* Legends live outside the scene so they never cover data */}
        <div className="sp-legend-row">
          <span className="sp-legend-item">
            <span className="sp-legend-grad" style={{ background: `linear-gradient(to right, ${RAMP[theme].join(',')})` }} />
            {dimLabel(channels.color)} 1 → 5
          </span>
          <span className="sp-legend-item">
            <svg width="34" height="18" viewBox="0 0 34 18">
              <circle cx={6} cy={9} r={3.5} className="sp-legend-dot" />
              <circle cx={22} cy={9} r={8} className="sp-legend-dot" />
            </svg>
            {dimLabel(channels.size)} 1 → 5
          </span>
          <span className="sp-legend-note">
            {t('sp.explorer.dragNote')} {t('sp.explorer.wheelNote')} {t('sp.labels.pinHint')}
            {excluded > 0 ? ` ${excluded} ${t('sp.explorer.hiddenNote')}` : ''}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Tab 3: Capability strength ─────────────────────────────────────

type CapSort = 'score' | 'avgVrio' | 'avgCrit' | 'highCount';

function CapabilityView({ items, capabilities, weights, theme, capFilter, onSelectResource }: {
  items: AnalysisResource[];
  capabilities: AnalysisCapability[];
  weights: Record<DimKey, number>;
  theme: 'light' | 'dark';
  capFilter: number | 'all';
  onSelectResource: (r: AnalysisResource) => void;
}) {
  const { t } = useT();
  const [sort, setSort] = useState<CapSort>('score');
  const [highT, setHighT] = useState(3.5);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const rows = useMemo(() => {
    const caps = capFilter === 'all' ? capabilities : capabilities.filter(c => c.id === capFilter);
    return caps.map(cap => {
      const linked = items
        .map(r => {
          const link = r.capabilities.find(c => c.id === cap.id);
          if (!link) return null;
          const comp = composite(r, weights);
          if (comp === null) return null;
          return { r, comp, criticality: link.criticality, product: comp * link.criticality };
        })
        .filter((x): x is NonNullable<typeof x> => x !== null)
        .sort((a, b) => b.product - a.product);

      if (linked.length === 0) return null;

      const avgCrit = linked.reduce((a, b) => a + b.criticality, 0) / linked.length;
      const critSum = linked.reduce((a, b) => a + b.criticality, 0);
      const avgVrio = critSum > 0
        ? linked.reduce((a, b) => a + b.comp * b.criticality, 0) / critSum
        : linked.reduce((a, b) => a + b.comp, 0) / linked.length;
      const highCount = linked.filter(l => l.comp >= highT).length;

      // Bounded 0–100: high-VRIO share (40) + avg VRIO (30) + avg criticality (30)
      const score = Math.round(
        40 * Math.min(highCount, 5) / 5 +
        30 * Math.max(0, avgVrio - 1) / 4 +
        30 * avgCrit
      );

      return { cap, linked, avgCrit, avgVrio, highCount, score };
    }).filter((x): x is NonNullable<typeof x> => x !== null)
      .sort((a, b) => {
        switch (sort) {
          case 'avgVrio': return b.avgVrio - a.avgVrio;
          case 'avgCrit': return b.avgCrit - a.avgCrit;
          case 'highCount': return b.highCount - a.highCount;
          default: return b.score - a.score;
        }
      });
  }, [items, capabilities, weights, capFilter, sort, highT]);

  const toggleExpand = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (rows.length === 0) {
    return (
      <div className="detail-card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--ink-3)' }}>
        {t('sp.cap.empty')}
      </div>
    );
  }

  return (
    <div>
      <div className="sp-view-controls">
        <label className="sp-inline-label">
          {t('sp.cap.sortBy')}
          <select className="form-select sp-channel-select" value={sort} onChange={e => setSort(e.target.value as CapSort)}>
            <option value="score">{t('sp.cap.sort.score')}</option>
            <option value="avgVrio">{t('sp.cap.sort.avgVrio')}</option>
            <option value="avgCrit">{t('sp.cap.sort.avgCrit')}</option>
            <option value="highCount">{t('sp.cap.sort.highCount')}</option>
          </select>
        </label>
        <label className="sp-inline-label">
          {t('sp.cap.highThreshold')}
          <input type="number" className="form-input sp-num" min={1} max={5} step={0.5} value={highT}
            onChange={e => setHighT(clamp(Number(e.target.value) || 3.5, 1, 5))} />
        </label>
        <span style={{ color: 'var(--ink-3)', fontSize: 'var(--t-11)' }}>
          {t('sp.cap.formula')}
        </span>
      </div>

      <div className="sp-cap-list">
        {rows.map(({ cap, linked, avgCrit, avgVrio, highCount, score }) => (
          <div key={cap.id} className="card sp-cap-card">
            <div className="sp-cap-header">
              <div className="sp-cap-meter" role="img" aria-label={`${t('sp.cap.scoreAria')} ${score}/100`}>
                <div className="sp-cap-meter-fill" style={{ width: `${score}%` }} />
              </div>
              <span className="sp-cap-score">{score}</span>
              <span className="sp-cap-title">{cap.title}</span>
              <span className="sp-cap-meta">{cap.domain} · {cap.nature}</span>
            </div>
            <div className="sp-cap-stats">
              {t('sp.cap.avgVrio')} {avgVrio.toFixed(2)} {t('sp.cap.weighted')} · {t('sp.cap.avgCrit')} {avgCrit.toFixed(2)} · {highCount} {t('sp.cap.highRes')} (≥ {highT})
            </div>
            <div className="sp-cap-resources">
              {(expanded.has(cap.id) ? linked : linked.slice(0, 3)).map((l, i) => (
                <button key={l.r.id} className="sp-cap-resource" onClick={() => onSelectResource(l.r)}>
                  <span className="sp-cap-resource-rank">{i + 1}.</span>
                  <TypeSwatch type={l.r.type} theme={theme} />
                  <span className="sp-cap-resource-name">{l.r.title}</span>
                  <span className="sp-cap-resource-math">{l.comp.toFixed(1)} × {l.criticality.toFixed(2)} = {l.product.toFixed(2)}</span>
                </button>
              ))}
              {linked.length > 3 && (
                <button className="btn btn-ghost btn-sm" onClick={() => toggleExpand(cap.id)}>
                  {expanded.has(cap.id) ? t('sp.cap.showTop') : `${t('sp.cap.showAll')} (${linked.length})`}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Shared detail card ─────────────────────────────────────────────

function ResourceDetailCard({ resource, weights, capabilities, theme, onClose }: {
  resource: AnalysisResource;
  weights: Record<DimKey, number>;
  capabilities: AnalysisCapability[];
  theme: 'light' | 'dark';
  onClose: () => void;
}) {
  const { t } = useT();
  const comp = composite(resource, weights);
  const capName = (id: number) => capabilities.find(c => c.id === id)?.title || `#${id}`;

  return (
    <div className="card sp-detail">
      <div className="sp-detail-header">
        <TypeSwatch type={resource.type} theme={theme} />
        <span className="sp-detail-title">{resource.title}</span>
        <span className="sp-detail-meta">
          {resource.type} · {resource.function} · {resource.raterCount} {t('sp.tip.raters')}
        </span>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>{t('common.close')}</button>
      </div>
      {resource.description && <p className="sp-detail-desc">{resource.description}</p>}
      <div className="sp-detail-grid">
        <div>
          <div className="sp-detail-section-label">{t('sp.detail.dims')}</div>
          {DIM_KEYS.map(k => {
            const agg = resource.dims[k];
            return (
              <div key={k} className="sp-dim-row">
                <span className="sp-dim-label">{t(`sp.dim.${k}`)}</span>
                <div className="sp-dim-bar">
                  <div className="sp-dim-bar-fill" style={{ width: agg.n > 0 ? `${((agg.median - 1) / 4) * 100}%` : 0 }} />
                </div>
                <span className="sp-dim-value">{agg.n > 0 ? agg.median.toFixed(1) : '—'}</span>
                {agg.flagged && <span className="sp-flag" title={`${t('sp.flag.disagree')} ${agg.spread}`}>⚑</span>}
              </div>
            );
          })}
          <div className="sp-dim-row sp-dim-row--total">
            <span className="sp-dim-label">{t('sp.detail.composite')}</span>
            <div className="sp-dim-bar">
              <div className="sp-dim-bar-fill" style={{ width: comp !== null ? `${((comp - 1) / 4) * 100}%` : 0 }} />
            </div>
            <span className="sp-dim-value">{comp !== null ? comp.toFixed(2) : '—'}</span>
          </div>
          <div className="sp-dim-row">
            <span className="sp-dim-label">{t('sp.detail.maturity')}</span>
            <div className="sp-dim-bar">
              <div className="sp-dim-bar-fill" style={{ width: resource.maturity !== null ? `${((resource.maturity - 1) / 4) * 100}%` : 0 }} />
            </div>
            <span className="sp-dim-value">{resource.maturity !== null ? resource.maturity.toFixed(1) : '—'}</span>
          </div>
        </div>
        <div>
          <div className="sp-detail-section-label">{t('sp.detail.caps')}</div>
          {resource.capabilities.length === 0 ? (
            <p style={{ color: 'var(--ink-3)', fontSize: 'var(--t-12)' }}>{t('sp.detail.noCaps')}</p>
          ) : (
            resource.capabilities.map(c => (
              <div key={c.id} className="sp-dim-row">
                <span className="sp-dim-label sp-dim-label--wide">{capName(c.id)}</span>
                <span className="sp-dim-value">{c.criticality.toFixed(2)}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
